// ─── Lag-window display helpers ──────────────────────────────────────────────
//
// A procedure edge can carry a minimum/maximum lag (in days) between a
// predecessor form completing and the next form being filled. The backend
// journey turns those into absolute `availableAt` (min) and `dueAt` (max)
// timestamps per step. These helpers turn those into the short "remaining time"
// notes shown on the procedure page.

import type { ProcedureJourneyStep } from '@/types/cgt'

export const DAY_MS = 86_400_000
const HOUR_MS = 3_600_000

function hoursToLabel(totalHours: number): string {
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  if (days > 0 && hours > 0) return `${days}d ${hours}h`
  if (days > 0) return `${days}d`
  return `${hours}h`
}

/**
 * Formats a positive ms **countdown** as "Xd Yh" / "Xd" / "Yh" — e.g. 1.5
 * days (129_600_000ms) → "1d 12h", 2 whole days → "2d", 30 minutes → "1h"
 * (rounds up to the hour, and never shows "0h", so an imminent deadline
 * still reads as time remaining rather than vanishing). Use `formatLagExact`
 * instead for an already-known, fixed duration (e.g. a configured lag
 * window) where a true "0h" is meaningful and must be shown as such.
 */
export function formatLag(ms: number): string {
  return hoursToLabel(Math.max(1, Math.ceil(ms / HOUR_MS)))
}

/**
 * Formats an exact ms duration as "Xd Yh" / "Xd" / "Yh" (no minimum floor) —
 * for a fixed, already-resolved value like a configured lag window, where an
 * explicit zero (e.g. "0h" = due immediately) is a real, distinct value from
 * "unconfigured" and must render as-is rather than being rounded up to "1h".
 */
export function formatLagExact(ms: number): string {
  return hoursToLabel(Math.round(ms / HOUR_MS))
}

export type LagTone = 'waiting' | 'due' | 'overdue'

export interface LagNote {
  text: string
  tone: LagTone
}

/**
 * Remaining-time note for a step's lag window, or null when there is nothing to
 * show (completed, or no lag configured). `waiting` = min lag not yet elapsed,
 * `due` = within the max-lag deadline, `overdue` = past the deadline.
 */
export function lagNote(
  step: Pick<ProcedureJourneyStep, 'status' | 'availableAt' | 'dueAt'>,
  now: number = Date.now(),
): LagNote | null {
  if (step.status === 'completed') return null

  if (step.availableAt) {
    const a = new Date(step.availableAt).getTime()
    if (now < a) return { text: `Available in ${formatLag(a - now)}`, tone: 'waiting' }
  }

  if (step.dueAt) {
    const d = new Date(step.dueAt).getTime()
    if (now > d) return { text: `Overdue by ${formatLag(now - d)}`, tone: 'overdue' }
    return { text: `Due in ${formatLag(d - now)}`, tone: 'due' }
  }

  return null
}
